import styled from 'styled-components';
import ThemeHelper from '../../themes/theme-helper';

const defaultValues = (theme) => {
  const { layout, colours, shadows, typography, palette } = theme;

  return {
    borderRadius: layout.borderRadius,
    fontSize: typography.size,
    lineHeight: typography.lineHeight,
    minWidth: '100px',
    colours: {
      text: typography.colour,
      background: '#fff',
      border: colours.grey[400],
      shadow: shadows.grey[500],
      hover: colours.grey[100],
      selected: {
        background: palette.primary.main,
        text: palette.primary.text
      },
      menuIcon: {
        background: null,
        border: null
      }
    },
    arrow: {
      width: `${layout.spacing * 2.5}px`
    },
    menuIcon: {
      padding: '0 3px'
    },
    menu: {
      shadowSize: '3px',
      minWidth: '120px',
      maxHeight: '320px',
      optionPadding: `${layout.spacing}px`,
      groupLabelPadding: `${layout.spacing / 2}px`
    }
  };
};

function getOverrides(theme) {
  return ThemeHelper.getComponentOverrides(
    theme,
    'dropdownMenu',
    defaultValues
  );
}

const DropdownWrapper = styled.div((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { typography } = theme;
  const { colours, fontSize, lineHeight, minWidth } = getOverrides(theme);

  return `
    position: relative;
    display: flex;
    font-family: ${typography.family};
    color: ${colours.text};
    font-size: ${fontSize};
    line-height: ${lineHeight};
    min-width: ${minWidth};
  `;
});

const DropdownToggle = styled.div`
  width: 100%;
`;

const SelectedOption = styled.div`
  height: 100%;
`;

const DownArrow = styled.span((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { arrow, fontSize } = getOverrides(theme);

  return `
    transform: rotate(180deg);
    width: ${arrow.width};
    flex-shrink: 0;

    svg {
      height: ${fontSize};
    }
  `;
});

const MenuButton = styled.div((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { borderRadius, menu, colours } = getOverrides(theme);

  const menuBorderRadius = props.icon
    ? `${borderRadius} 0 0 ${borderRadius};`
    : borderRadius;

  return `
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: ${menuBorderRadius};
    padding: ${menu.optionPadding};
    -webkit-appearance: none;
    border: 1px solid ${colours.border};
    box-sizing: border-box;
    height: 100%;
    max-width: 100%;
    background-color: ${colours.background};
    color: ${colours.text};
    cursor: pointer;
    ${props.disabled &&
      `
      cursor: not-allowed;
      box-shadow: none;
      opacity: 0.65;

      &:focus {
          outline: 0;
      }
    `}
    &:active {
        border-style: solid;
    }

    span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  `;
});

const DropdownMenu = styled.div((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { zIndex } = theme;
  const { borderRadius, menu, colours } = getOverrides(theme);

  return `
    display: ${props.showMenu ? 'block' : 'none'};
    position: absolute;
    border-radius: ${borderRadius};
    overflow: hidden;
    box-shadow: ${menu.shadowSize} ${menu.shadowSize} ${colours.shadow};
    background-color: ${colours.background};
    width: auto;
    min-width: 100%;
    max-height: ${menu.maxHeight};
    overflow-y: auto;
    z-index: ${zIndex.dropdown};
    border: 1px solid ${colours.border};

    ul {
      border-radius: ${borderRadius};
      overflow: hidden;
      list-style: none;
      margin: 0;
      padding: 0;
    }
  `;
});

const OptionGroupLabel = styled.li((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { menu, colours, lineHeight } = getOverrides(theme);

  return `
    padding: ${menu.groupLabelPadding};
    border: none;
    width: 100%;
    text-align: left;
    background-color: ${colours.background};
    border-radius: 0;
    color: ${colours.text};
    font-size: 14px;
    line-height: ${lineHeight};
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-weight: bold;

    &:not(:first-child) {
      margin-top: 10px;
    }
  `;
});

const MenuItem = styled.li((props) => {
  const theme = ThemeHelper.getTheme(props);
  const { layout } = theme;
  const { menu, colours, fontSize, lineHeight } = getOverrides(theme);

  return `
    button {
      padding: ${menu.optionPadding};
      padding-left: ${layout.spacing * 1.5}px;
      border: none;
      width: 100%;
      text-align: left;
      background-color: ${colours.background};
      border-radius: 0;
      color: ${colours.text};
      font-size: ${fontSize};
      line-height: ${lineHeight};
      white-space: nowrap;
      overflow: hidden;
      /* extra padding is applied to firefox and safari as scrollbar appears on top of content */
      @supports (-moz-appearance:none) {
        padding-right: 20px; /* firefox only */
      }
      @supports (-webkit-hyphens:none) {
        padding-right: 20px; /* safari only */
      }

      &:hover {
          background-color: ${colours.hover};
      }
    }

    ${props.selected &&
      `
        button,
        button:hover {
          color: ${colours.selected.text};
          background-color: ${colours.selected.background};
          border-color: ${colours.selected.background};
        }
    `}
  `;
});

export {
  DropdownWrapper,
  DropdownMenu,
  DropdownToggle,
  SelectedOption,
  DownArrow,
  MenuButton,
  MenuItem,
  OptionGroupLabel
};
